import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { dateTime, mobile as fmtMobile } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Card, CardHead, Chips, Notice, Select, useToast } from '../components/ui';

type Mode = 'OPEN' | 'CONSENT' | 'HIDDEN' | 'OFF';
type Kind = 'INDIVIDUAL' | 'VENDOR' | 'PROJECT';
interface TypePolicy {
  chat: boolean;
  call: Mode;
  whatsapp: Mode;
}
type Policy = Record<Kind, TypePolicy>;

const KINDS: Array<{ key: Kind; label: string; sub: string }> = [
  { key: 'INDIVIDUAL', label: 'Individual property owner', sub: 'Listed by: Owner, and every listing that is not a property' },
  { key: 'VENDOR', label: 'Regular vendor', sub: 'Listed by: Dealer' },
  { key: 'PROJECT', label: 'Project property', sub: 'Listed by: Builder' },
];

const MODES: Array<{ value: Mode; label: string }> = [
  { value: 'OPEN', label: 'Open to everyone' },
  { value: 'CONSENT', label: 'Shown; opens after the lister agrees in chat' },
  { value: 'HIDDEN', label: 'Hidden until the lister agrees in chat' },
  { value: 'OFF', label: 'Off' },
];

interface RevealRow {
  id: string;
  channel: 'CALL' | 'WHATSAPP';
  createdAt: string;
  revokedAt: string | null;
  revealer: { id: string; displayName: string | null; mobile: string };
  revealedTo: { id: string; displayName: string | null; mobile: string };
  listing: { id: string; title: string } | null;
}

/**
 * Contact Visibility Management: how customers can reach each kind of lister
 * - chat, call, WhatsApp - and every time a lister let somebody have their
 * number. The server applies the rules: a number they keep hidden is in no
 * response the app or anything else can read.
 */
export function ContactVisibilityPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const current = useQuery({ queryKey: ['contact-policy'], queryFn: () => api.get<Policy>('/admin/contact-policy') });
  const [draft, setDraft] = useState<Policy | null>(null);
  useEffect(() => {
    if (current.data) setDraft(current.data);
  }, [current.data]);

  const save = useMutation({
    mutationFn: (p: Policy) => api.put<Policy>('/admin/contact-policy', p),
    onSuccess: () => {
      toast('Saved. The app follows it within a minute.', 'success');
      qc.invalidateQueries({ queryKey: ['contact-policy'] });
      qc.invalidateQueries({ queryKey: ['contact-reveals'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const set = (kind: Kind, patch: Partial<TypePolicy>) => setDraft((d) => (d ? { ...d, [kind]: { ...d[kind], ...patch } } : d));
  const changed = draft && current.data && JSON.stringify(draft) !== JSON.stringify(current.data);

  return (
    <div className="stack">
      <Notice tone="info" title="Who listed a property decides how customers can reach them">
        Customers&apos; own details are never shown to other customers. A listing&apos;s number is sent to the app only when a
        channel below lets this viewer use it - otherwise it is not in the response at all.
      </Notice>
      <Card>
        <CardHead title="Contact rules" sub="Chat, Call and WhatsApp for each kind of lister" />
        {!draft ? (
          <div className="cell-muted">Loading…</div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Lister</th>
                  <th>Chat</th>
                  <th>Call</th>
                  <th>WhatsApp</th>
                </tr>
              </thead>
              <tbody>
                {KINDS.map((k) => (
                  <tr key={k.key}>
                    <td>
                      <div className="cell-strong">{k.label}</div>
                      <div className="cell-muted">{k.sub}</div>
                    </td>
                    <td>
                      <label className="row" style={{ gap: 6 }}>
                        <input type="checkbox" checked={draft[k.key].chat} onChange={(e) => set(k.key, { chat: e.target.checked })} />
                        {draft[k.key].chat ? 'On' : 'Off'}
                      </label>
                    </td>
                    <td>
                      <Select options={MODES} value={draft[k.key].call} onChange={(e) => set(k.key, { call: e.target.value as Mode })} />
                    </td>
                    <td>
                      <Select options={MODES} value={draft[k.key].whatsapp} onChange={(e) => set(k.key, { whatsapp: e.target.value as Mode })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="row" style={{ gap: 'var(--s3)', marginTop: 'var(--s4)', justifyContent: 'flex-end' }}>
          <Button disabled={!changed} onClick={() => current.data && setDraft(current.data)}>
            Undo changes
          </Button>
          <Button variant="primary" disabled={!changed} loading={save.isPending} onClick={() => draft && save.mutate(draft)}>
            Save rules
          </Button>
        </div>
      </Card>
      <Reveals />
    </div>
  );
}

function Reveals() {
  const { get, set } = useListParams();
  const status = get('status');
  const columns: Array<Column<RevealRow>> = [
    {
      key: 'who',
      header: 'Shared by',
      render: (r) => (
        <div>
          <div className="cell-strong">{r.revealer.displayName ?? 'Unnamed'}</div>
          <div className="cell-muted">{fmtMobile(r.revealer.mobile)}</div>
        </div>
      ),
    },
    {
      key: 'with',
      header: 'With',
      render: (r) => (
        <div>
          <div className="cell-strong">{r.revealedTo.displayName ?? 'Unnamed'}</div>
          <div className="cell-muted">{fmtMobile(r.revealedTo.mobile)}</div>
        </div>
      ),
    },
    { key: 'channel', header: 'For', render: (r) => (r.channel === 'CALL' ? 'Calls' : 'WhatsApp') },
    { key: 'listing', header: 'Listing', render: (r) => <span className="cell-muted">{r.listing?.title ?? '—'}</span> },
    { key: 'when', header: 'When', render: (r) => <span className="cell-muted">{dateTime(r.createdAt)}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (r) =>
        r.revokedAt ? (
          <div>
            <Badge tone="neutral">Withdrawn</Badge>
            <div className="cell-muted">{dateTime(r.revokedAt)}</div>
          </div>
        ) : (
          <Badge tone="green" dot>
            Shared
          </Badge>
        ),
    },
  ];
  return (
    <DataTable<RevealRow>
      queryKey={['contact-reveals', status]}
      path="/admin/contact-reveals"
      params={{ status }}
      columns={columns}
      emptyIcon="📞"
      emptyTitle="No numbers shared yet"
      emptyBody="When a lister agrees in a chat to let a customer call or WhatsApp them, it is recorded here."
      toolbar={
        <Chips
          value={status}
          onChange={(v) => set('status', v)}
          options={[
            { value: '', label: 'All' },
            { value: 'ACTIVE', label: 'Shared' },
            { value: 'REVOKED', label: 'Withdrawn' },
          ]}
        />
      }
    />
  );
}
